import { useState, useMemo } from "react";
import { Card, Row, Col, Typography, Input, Select, Button, Table, Badge, Tooltip as AntdTooltip } from "antd";
import { UserOutlined, SearchOutlined, AimOutlined, CheckCircleOutlined, RiseOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Legend, LabelList } from "recharts";

const { Title, Text } = Typography;
const { Option } = Select;

const VistaART = ({ data, onBack }) => {
  const [busqueda, setBusqueda] = useState("");
  const [municipio, setMunicipio] = useState("todos");
  const [departamento, setDepartamento] = useState("todos");
  const [proyecto, setProyecto] = useState("todos");
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  const participantesFiltrados = useMemo(() => {
    return data.participantes.filter((p) => {
      const busca = busqueda === "" || p.nombre.toLowerCase().includes(busqueda.toLowerCase()) || p.cedula.includes(busqueda);
      const muni = municipio === "todos" || p.municipio === municipio;
      const depto = departamento === "todos" || p.departamento === departamento;
      const proy = proyecto === "todos" || p.proyectos === proyecto;
      return busca && muni && depto && proy;
    });
  }, [data.participantes, busqueda, municipio, departamento, proyecto]);

  const metricas = useMemo(() => {
    const total = participantesFiltrados.length;
    const conArchivos = participantesFiltrados.filter((p) => p.tiene_archivos).length;
    const totalRegistros = participantesFiltrados.reduce((sum, p) => {
      return (
        sum +
        Object.values(p.resultados || {}).reduce(
          (ps, proyecto) => ps + (proyecto.total || 0),
          0
        )
      );
    }, 0);
    let metaGeneral = 0;
    if (busqueda.trim()) {
      if (total === 1) {
        metaGeneral = Math.round(Number(participantesFiltrados[0].meta_x_participante) || 0);
      } else {
        metaGeneral = Math.round(
          participantesFiltrados.reduce(
            (acc, p) => acc + (Number(p.meta_x_participante) || 0),
            0
          )
        );
      }
    } else {
      const metas = new Set();
      participantesFiltrados.forEach((p) => metas.add(Number(p.meta_x_proyecto) || 0));
      metaGeneral = Math.round([...metas].reduce((acc, m) => acc + m, 0));
    }
    const archivos = totalRegistros;

    const porcentajeAvance = metaGeneral > 0
      ? Math.round((archivos / metaGeneral) * 100)
      : 0;

    return {
      total,
      conArchivos,
      totalRegistros,
      metaGeneral,
      archivosEjecutados: archivos,
      porcentajeAvance,
    };
  }, [participantesFiltrados, busqueda]);

  const datosProyectosMetas = useMemo(() => {
    const map = new Map();
    participantesFiltrados.forEach((p) => {
      if (!map.has(p.proyectos)) map.set(p.proyectos, { nombre: p.proyectos, meta: p.meta_x_proyecto, ejecutado: 0 });
      const item = map.get(p.proyectos);
      Object.values(p.resultados || {}).forEach((pr) => item.ejecutado += pr.total || 0);
    });
    return [...map.values()].map(p => ({
      ...p,
      nombreCorto: p.nombre,
      porcentaje: p.meta > 0 ? Math.round((p.ejecutado / p.meta) * 100) : 0
    }));
  }, [participantesFiltrados]);

  const datosParticipantesMetas = useMemo(() => participantesFiltrados.map(p => {
    const meta = parseFloat(p.meta_x_participante);
    const ejecutado = Object.values(p.resultados || {}).reduce((s, pr) => s + (pr.total || 0), 0);
    const cumplido = meta > 0 ? Math.round((ejecutado / meta) * 100) : 0;
    return {
      ...p,
      metaIndividual: meta,
      archivosParticipante: ejecutado,
      porcentajeCumplido: cumplido,
      porcentajeFaltante: Math.max(0, 100 - cumplido),
      estado: cumplido >= 100 ? "Completado" : cumplido >= 50 ? "En progreso" : "Atrasado"
    };
  }), [participantesFiltrados]);

  const limpiarFiltros = () => {
    setBusqueda("");
    setMunicipio("todos");
    setDepartamento("todos");
    setProyecto("todos");
  };

  const columns = [
    {
      title: "#",
      key: "index",
      align: "center",
      render: (_text, _record, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    { title: "Nombre", dataIndex: "nombre", key: "nombre" },
    { title: "Cédula", dataIndex: "cedula", key: "cedula" },
    { title: "Municipio", dataIndex: "municipio", key: "municipio" },

    {
      title: "Meta",
      dataIndex: "metaIndividual",
      align: "center",
      render: (val) => (
        <Badge
          count={isNaN(val) || val == null ? 0 : val}
          style={{ backgroundColor: isNaN(val) ? "#d4d4d8" : "#1677ff" }}
        />
      ),
    },
    {
      title: "Subidos",
      dataIndex: "archivosParticipante",
      align: "center",
      render: (val) => {
        const count = (typeof val === "number" && !isNaN(val)) ? val : 0;
        const color = count === 0 ? "#a1a1aa" : "#10b981";
        return (
          <Badge
            count={count}
            style={{
              backgroundColor: color,
              color: "#fff",
            }}
          />
        );
      },
    },
    {
      title: "% Cumplido",
      dataIndex: "porcentajeCumplido",
      align: "center",
      render: (val) => {
        const num = isNaN(val) || val == null ? 0 : val;
        let color = "#a1a1aa"; // gris
        if (!isNaN(num)) {
          if (num >= 100) color = "#16a34a"; // verde
          else if (num >= 70) color = "#facc15"; // amarillo
          else if (num >= 50) color = "#fb923c"; // naranja
          else color = "#ef4444"; // rojo
        }
        return (
          <Badge
            count={num + "%"}
            style={{ backgroundColor: color, color: "#fff" }}
          />
        );
      },
    },

    {
      title: "% Faltante",
      dataIndex: "porcentajeFaltante",
      align: "center",
      render: (val) => {
        const num = isNaN(val) || val == null ? 100 : val;
        let color = "#a1a1aa"; // gris
        if (num < 20) color = "#16a34a"; // verde claro
        else if (num < 50) color = "#facc15"; // amarillo
        else if (num < 70) color = "#fb923c"; // naranja
        else color = "#ef4444"; // rojo

        return (
          <Badge
            count={num + "%"}
            style={{ backgroundColor: color, color: "#fff" }}
          />
        );
      },
    },
    {
      title: "Estado",
      dataIndex: "estado",
      align: "center",
      render: (estado) => {
        let color = "#a1a1aa";
        if (estado === "Completado") color = "green";
        else if (estado === "En progreso") color = "orange";
        else if (estado === "Atrasado") color = "red";
        return <Badge color={color} text={estado || "Sin dato"} />;
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={4}>
          <Input
            prefix={<SearchOutlined />}
            placeholder="Nombre o cédula..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
        </Col>
        <Col span={4}>
          <Select value={municipio} onChange={setMunicipio} style={{ width: "100%" }}>
            <Option value="todos">Todos los municipios</Option>
            {data.municipios.map((m) => <Option key={m} value={m}>{m}</Option>)}
          </Select>
        </Col>
        <Col span={6}>
          <Select value={departamento} onChange={setDepartamento} style={{ width: "100%" }}>
            <Option value="todos">Todos los departamentos</Option>
            {data.departamentos.map((d) => <Option key={d} value={d}>{d}</Option>)}
          </Select>
        </Col>
        <Col span={6}>
          <Select value={proyecto} onChange={setProyecto} style={{ width: "100%" }}>
            <Option value="todos">Todos los proyectos</Option>
            {data.proyectosUnicos.map((p) => <Option key={p} value={p}>{p}</Option>)}
          </Select>
        </Col>
        <Col span={4}>
          <Button onClick={limpiarFiltros} block>Limpiar Filtros</Button>
        </Col>
      </Row>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6} lg={6}>
          <Card bordered hoverable style={{ height: 120, padding: 10 }}>
            <div style={{ textAlign: "center" }}>
              <UserOutlined style={{ fontSize: 24, color: "#3b82f6" }} />
              <Title level={4} style={{ marginTop: 10 }}>Total Participantes</Title>
              <p style={{ fontSize: 18, fontWeight: 500 }}>{metricas.total}</p>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={6}>
          <Card bordered hoverable style={{ height: 120, padding: 10 }}>
            <div style={{ textAlign: "center" }}>
              <AimOutlined style={{ fontSize: 24, color: "#3b82f6" }} />
              <Title level={4} style={{ marginTop: 10 }}>Visitas Proyectadas</Title>
              <p style={{ fontSize: 18, fontWeight: 500 }}>{metricas.metaGeneral}</p>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={6}>
          <Card bordered hoverable style={{ height: 120, padding: 10 }}>
            <div style={{ textAlign: "center" }}>
              <CheckCircleOutlined style={{ fontSize: 24, color: "#10b981" }} />
              <Title level={4} style={{ marginTop: 10 }}>Visitas Realizadas</Title>
              <p style={{ fontSize: 18, fontWeight: 500 }}>{metricas.archivosEjecutados}</p>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={6}>
          <Card bordered hoverable style={{ height: 120, padding: 10 }}>
            <div style={{ textAlign: "center" }}>
              <RiseOutlined style={{ fontSize: 24, color: "#f59e0b" }} />
              <Title level={4} style={{ marginTop: 10 }}>% de Avance</Title>
              <p style={{ fontSize: 18, fontWeight: 500 }}>{metricas.porcentajeAvance}%</p>
            </div>
          </Card>
        </Col>
      </Row>
      <Row gutter={24} style={{ marginBottom: 24 }}>
        <Col xs={24} md={24}>
          <Card
            title={
              <span>
                Visitas de acompañamiento tecnico por proyecto
                <AntdTooltip
                  title={
                    <div>
                      <p><strong>Meta:</strong> Total proyectado por proyecto.</p>
                      <p><strong>Ejecutado:</strong> Número real alcanzado.</p>
                      <p><strong>Comparación:</strong> Cada barra muestra comparación directa.</p>
                    </div>
                  }
                >
                  <InfoCircleOutlined style={{ color: "#3b82f6", marginLeft: 8 }} />
                </AntdTooltip>
              </span>
            }
          >
            <ResponsiveContainer width="100%" height={40 * datosProyectosMetas.length + 100}>
              <BarChart
                data={datosProyectosMetas}
                layout="vertical"
                margin={{ top: 20, right: 30, left: 10, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="4 4" />
                <XAxis type="number" domain={[0, 5000]} />
                <YAxis
                  dataKey="nombreCorto"
                  type="category"
                  width={300}
                />
                <RechartsTooltip />
                <Legend verticalAlign="top" height={36} />
                <Bar dataKey="meta" name="Meta" fill="#3b82f6">
                  <LabelList dataKey="meta" position="right" />
                </Bar>
                <Bar dataKey="ejecutado" name="Ejecutado" fill="#10b981">
                  <LabelList dataKey="ejecutado" position="right" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>
      <Card title="Análisis de Metas por Participante">
        <Text type="secondary" style={{ display: "block", marginBottom: 12, marginLeft: 25, marginTop: 10 }}>
          Mostrando {datosParticipantesMetas.length} participantes filtrados
        </Text>
        <Table
          dataSource={datosParticipantesMetas}
          columns={columns}
          rowKey="oid"
          pagination={{
            ...pagination,
            pageSizeOptions: ["10", "20", "30", "40", "50"],
            showSizeChanger: true,
            onChange: (page, pageSize) => setPagination({ current: page, pageSize }),
          }}
          scroll={{ y: 400, x: 800 }}
        />
      </Card>
    </div>
  );
};

export default VistaART;
